import * as api from '@opentelemetry/api';
import { Attributes, AttributeValue } from '@opentelemetry/api';
import { Buffer } from 'buffer';
import { Span } from './Span';

export enum TraceFlags {
  /** Represents no flag set. */
  NONE = 0x0,
  /** Bit to represent whether trace is sampled in trace flags. */
  // tslint:disable-next-line: no-bitwise
  SAMPLED = 0x1 << 0,
}

interface IdGenerator {
  /** Returns a trace ID composed of 32 lowercase hex characters. */
  generateTraceId(): string;
  /** Returns a span ID composed of 16 lowercase hex characters. */
  generateSpanId(): string;
}

const SPAN_ID_BYTES = 8;
const TRACE_ID_BYTES = 16;

export class RandomIdGenerator implements IdGenerator {
  /**
   * Returns a random 16-byte trace ID formatted/encoded as a 32 lowercase hex
   * characters corresponding to 128 bits.
   */
  public generateTraceId: () => string = getIdGenerator(TRACE_ID_BYTES);

  /**
   * Returns a random 8-byte span ID formatted/encoded as a 16 lowercase hex
   * characters corresponding to 64 bits.
   */
  public generateSpanId: () => string = getIdGenerator(SPAN_ID_BYTES);
}

const SHARED_BUFFER = Buffer.allocUnsafe(TRACE_ID_BYTES);
function getIdGenerator(bytes: number): () => string {
  return function generateId(): string {
    for (let i = 0; i < bytes / 4; i++) {
      // unsigned right shift drops decimal part of the number
      // it is required because if a number between 2**32 and 2**32 - 1 is generated, an out of range error is thrown by writeUInt32BE
      // tslint:disable-next-line: no-bitwise
      SHARED_BUFFER.writeUInt32BE((Math.random() * 2 ** 32) >>> 0, i * 4);
    }

    // If buffer is all 0, set the last byte to 1 to guarantee a valid w3c id is generated
    for (let i = 0; i < bytes; i++) {
      if (SHARED_BUFFER[i] > 0) {
        break;
      } else if (i === bytes - 1) {
        SHARED_BUFFER[bytes - 1] = 1;
      }
    }

    return SHARED_BUFFER.toString('hex', 0, bytes);
  };
}

/**
 * Get the parent to assign to a started span. If options.parent is null,
 * do not assign a parent.
 *
 * @param options span options
 * @param context context to check for parent
 */
function getParent(options: api.SpanOptions, context: api.Context): api.SpanContext | undefined {
  if (options.parent === null) {
    return undefined;
  }
  if (options.parent) {
    return getContext(options.parent);
  }
  return api.getParentSpanContext(context);
}

function getContext(span: api.Span | api.SpanContext): api.SpanContext {
  return isSpan(span) ? span.context() : span;
}

function isSpan(span: api.Span | api.SpanContext): span is api.Span {
  return typeof (span as api.Span).context === 'function';
}

function sanitizeAttributes(attributes: unknown): Attributes {
  const out: Attributes = {};

  if (attributes === null || typeof attributes !== 'object') {
    return out;
  }

  for (const [k, v] of Object.entries(attributes!)) {
    if (isAttributeValue(v)) {
      if (Array.isArray(v)) {
        out[k] = v.slice();
      } else {
        out[k] = v;
      }
    }
  }

  return out;
}

export function isAttributeValue(val: unknown): val is AttributeValue {
  if (val === null) {
    return true;
  }

  if (Array.isArray(val)) {
    return isHomogeneousAttributeValueArray(val);
  }

  return isValidPrimitiveAttributeValue(val);
}

function isHomogeneousAttributeValueArray(arr: unknown[]): boolean {
  let type: string | undefined;

  for (const element of arr) {
    // null/undefined elements are allowed
    if (element === null) {
      continue;
    }

    if (!type) {
      if (isValidPrimitiveAttributeValue(element)) {
        type = typeof element;
        continue;
      }
      // encountered an invalid primitive
      return false;
    }

    if (typeof element === type) {
      continue;
    }

    return false;
  }

  return true;
}

function isValidPrimitiveAttributeValue(val: unknown): boolean {
  // tslint:disable-next-line: switch-default
  switch (typeof val) {
    case 'number':
      return true;
    case 'boolean':
      return true;
    case 'string':
      return true;
  }

  return false;
}

export class Tracer {
  private readonly idGenerator: IdGenerator;
  public constructor() {
    this.idGenerator = new RandomIdGenerator();
  }

  public startSpan(name: string, options: api.SpanOptions = {}, context: api.Context = api.context.active()): Span {
    const parentContext = getParent(options, context);
    const spanId = this.idGenerator.generateSpanId();
    let traceId;
    let traceState;
    if (!parentContext || !api.trace.isSpanContextValid(parentContext)) {
      // New root span.
      traceId = this.idGenerator.generateTraceId();
    } else {
      // New child span.
      traceId = parentContext.traceId;
      traceState = parentContext.traceState;
    }

    const spanKind = options.kind ?? api.SpanKind.INTERNAL;
    // const links = options.links ?? [];
    const attributes = sanitizeAttributes(options.attributes);
    // make sampling decision

    const traceFlags = api.TraceFlags.NONE;
    const spanContext = { traceId, spanId, traceFlags, traceState };

    // const span = new Span(this, name, spanContext, spanKind, parentContext ? parentContext.spanId : undefined, links, options.startTime);
    const span = new Span(name, spanContext, spanKind);
    // Set default attributes
    span.setAttributes(Object.assign(attributes, options.attributes));
    return span;
  }
}
